import type { ToolContext, ToolDef } from "@fabric-harness/sdk";
import type { GenieClient } from "@databricks/sdk-genie/v1";
import type { DatabricksRawProtocolClient } from "./raw-protocol-client.js";

export type DatabricksGenieSdkClient = Pick<
  GenieClient,
  | "genieCreateConversationMessage"
  | "genieStartConversation"
  | "genieGetConversationMessage"
  | "genieGetMessageAttachmentQueryResult"
  | "genieListConversations"
  | "genieListConversationMessages"
  | "genieCreateMessageComment"
  | "genieListMessageComments"
  | "genieDeleteConversation"
>;

export type DatabricksGenieMessageStatus =
  | "FETCHING_METADATA"
  | "FILTERING_CONTEXT"
  | "ASKING_AI"
  | "PENDING_WAREHOUSE"
  | "EXECUTING_QUERY"
  | "FAILED"
  | "COMPLETED"
  | "SUBMITTED"
  | "QUERY_RESULT_EXPIRED"
  | "CANCELLED"
  | string;

export interface DatabricksGenieAgentReference {
  /** Canonical Genie Agent id. */
  agentId: string;
}

export interface DatabricksGenieClientOptions extends DatabricksGenieAgentReference {
  /**
   * Explicit fallback for the 0.21 SDK query-result schema defect. Used only when the generated
   * parser rejects Databricks' documented row-array payload; other SDK errors remain fail-closed.
   */
  rawProtocolClient?: DatabricksRawProtocolClient;
  pollIntervalMs?: number;
  timeoutMs?: number;
  /** Random polling variation from 0 to 1. Default 0.2. */
  pollJitterRatio?: number;
  /** Maximum attachments accepted from one message. Default 32. */
  maxAttachments?: number;
  /** Maximum unknown attachment kinds retained. Default 8. */
  maxUnknownAttachments?: number;
  /** Maximum JSON-encoded query-result size. Default 2 MiB. */
  maxResultBytes?: number;
  /** Maximum combined text answer size. Default 100,000 characters. */
  maxTextChars?: number;
}

export interface DatabricksGenieToolOptions extends DatabricksGenieClientOptions {
  name?: string;
  description?: string;
  /** Ask Genie to generate visualization attachments. Beta in Databricks. */
  enableVisualization?: boolean;
}

export interface DatabricksGenieAskInput {
  question: string;
  conversationId?: string;
  enableVisualization?: boolean;
  signal?: AbortSignal;
}

export interface DatabricksGenieTextAttachment {
  kind: "text";
  attachmentId?: string;
  content: string;
}

export interface DatabricksGenieQueryResult {
  statementResponse?: Record<string, unknown>;
  raw: Record<string, unknown>;
}

export interface DatabricksGenieQueryAttachment {
  kind: "query";
  attachmentId: string;
  query?: string;
  title?: string;
  description?: string;
  statementId?: string;
  queryResultMetadata?: Record<string, unknown>;
  result: DatabricksGenieQueryResult;
}

export interface DatabricksGenieVisualizationAttachment {
  kind: "visualization";
  attachmentId: string;
  title?: string;
  queryAttachmentId?: string;
  /** Relative API path for callers that need the PNG payload. */
  downloadPath: string;
}

export interface DatabricksGenieSuggestedQuestionsAttachment {
  kind: "suggested-questions";
  attachmentId?: string;
  questions: string[];
}

export interface DatabricksGenieUnknownAttachment {
  kind: "unknown";
  attachmentId?: string;
  raw: Record<string, unknown>;
}

export type DatabricksGenieAttachment =
  | DatabricksGenieTextAttachment
  | DatabricksGenieQueryAttachment
  | DatabricksGenieVisualizationAttachment
  | DatabricksGenieSuggestedQuestionsAttachment;

export interface DatabricksGenieResponse {
  agentId: string;
  conversationId: string;
  messageId: string;
  status: "COMPLETED";
  attachments: DatabricksGenieAttachment[];
  unknownAttachments: DatabricksGenieUnknownAttachment[];
  unknownAttachmentsTruncated: boolean;
  /** Compatibility summary of all text attachments. */
  text?: string;
  /** Compatibility summary of the first generated query. */
  sql?: string;
  /** Compatibility result for the first generated query. */
  result?: Record<string, unknown>;
}

export interface DatabricksGenieConversationSummary {
  conversationId: string;
  title?: string;
  createdTimestamp?: number;
}

export interface DatabricksGenieConversationPage {
  conversations: DatabricksGenieConversationSummary[];
  nextPageToken?: string;
}

export interface DatabricksGenieMessageSummary {
  messageId: string;
  conversationId?: string;
  content?: string;
  status?: DatabricksGenieMessageStatus;
  createdTimestamp?: number;
  lastUpdatedTimestamp?: number;
  error?: Record<string, unknown>;
}

export interface DatabricksGenieMessagePage {
  messages: DatabricksGenieMessageSummary[];
  nextPageToken?: string;
}

export interface DatabricksGenieMessageComment {
  messageCommentId: string;
  conversationId?: string;
  messageId?: string;
  content: string;
  createdTimestamp?: number;
  userId?: number;
}

export interface DatabricksGenieCommentPage {
  comments: DatabricksGenieMessageComment[];
  nextPageToken?: string;
}

export class DatabricksGenieProtocolError extends Error {
  constructor(message: string) {
    super(`Databricks Genie protocol error: ${message}`);
    this.name = "DatabricksGenieProtocolError";
  }
}

export class DatabricksGenieTimeoutError extends Error {
  readonly conversationId: string;
  readonly messageId: string;

  constructor(conversationId: string, messageId: string, timeoutMs: number) {
    super(
      `Databricks Genie message ${messageId} in conversation ${conversationId} did not reach a terminal state within ${timeoutMs}ms.`,
    );
    this.name = "DatabricksGenieTimeoutError";
    this.conversationId = conversationId;
    this.messageId = messageId;
  }
}

export class DatabricksGenieMessageError extends Error {
  readonly conversationId: string;
  readonly messageId: string;
  readonly status: DatabricksGenieMessageStatus;
  readonly details: Record<string, unknown> | undefined;

  constructor(
    conversationId: string,
    messageId: string,
    status: DatabricksGenieMessageStatus,
    details?: Record<string, unknown>,
  ) {
    super(
      `Databricks Genie message ${messageId} in conversation ${conversationId} ended in ${status}${errorDetail(details)}.`,
    );
    this.name = "DatabricksGenieMessageError";
    this.conversationId = conversationId;
    this.messageId = messageId;
    this.status = status;
    this.details = details;
  }
}

export class DatabricksGenieResponseLimitError extends Error {
  constructor(kind: string, limit: number) {
    super(`Databricks Genie ${kind} exceeded the configured limit of ${limit}.`);
    this.name = "DatabricksGenieResponseLimitError";
  }
}

interface RawGenieAttachment {
  attachment_id?: unknown;
  text?: unknown;
  query?: unknown;
  viz?: unknown;
  suggested_questions?: unknown;
  [key: string]: unknown;
}

interface RawGenieMessage {
  message_id?: unknown;
  id?: unknown;
  conversation_id?: unknown;
  content?: unknown;
  status?: unknown;
  attachments?: unknown;
  error?: unknown;
  created_timestamp?: unknown;
  last_updated_timestamp?: unknown;
}

interface ParsedAttachments {
  attachments: DatabricksGenieAttachment[];
  unknownAttachments: DatabricksGenieUnknownAttachment[];
  unknownAttachmentsTruncated: boolean;
}

const enc = encodeURIComponent;
const FAILURE_STATES = new Set(["FAILED", "CANCELLED", "QUERY_RESULT_EXPIRED"]);

export class DatabricksGenieClient {
  readonly agentId: string;
  private readonly basePath: string;
  private readonly pollIntervalMs: number;
  private readonly timeoutMs: number;
  private readonly pollJitterRatio: number;
  private readonly maxAttachments: number;
  private readonly maxUnknownAttachments: number;
  private readonly maxResultBytes: number;
  private readonly maxTextChars: number;
  private readonly rawProtocolClient: DatabricksRawProtocolClient | undefined;

  constructor(
    private readonly client: DatabricksGenieSdkClient,
    options: DatabricksGenieClientOptions,
  ) {
    this.agentId = resolveAgentId(options);
    this.rawProtocolClient = options.rawProtocolClient;
    this.basePath = `/api/2.0/genie/spaces/${enc(this.agentId)}`;
    this.pollIntervalMs = positiveInteger(options.pollIntervalMs ?? 2_000, "pollIntervalMs");
    this.timeoutMs = positiveInteger(options.timeoutMs ?? 120_000, "timeoutMs");
    this.pollJitterRatio = boundedRatio(options.pollJitterRatio ?? 0.2, "pollJitterRatio");
    this.maxAttachments = positiveInteger(options.maxAttachments ?? 32, "maxAttachments");
    this.maxUnknownAttachments = nonNegativeInteger(
      options.maxUnknownAttachments ?? 8,
      "maxUnknownAttachments",
    );
    this.maxResultBytes = positiveInteger(
      options.maxResultBytes ?? 2 * 1024 * 1024,
      "maxResultBytes",
    );
    this.maxTextChars = positiveInteger(options.maxTextChars ?? 100_000, "maxTextChars");
  }

  async ask(input: DatabricksGenieAskInput): Promise<DatabricksGenieResponse> {
    const question = nonEmpty(input.question, "question");
    const payload = {
      content: question,
      ...(input.enableVisualization !== undefined
        ? { enableVisualization: input.enableVisualization }
        : {}),
    };

    let conversationId: string;
    let messageId: string;
    if (input.conversationId) {
      conversationId = nonEmpty(input.conversationId, "conversationId");
      const waiter = await this.callMutation<
        Awaited<ReturnType<DatabricksGenieSdkClient["genieCreateConversationMessage"]>>
      >(() =>
        this.client.genieCreateConversationMessage(
          { spaceId: this.agentId, conversationId, ...payload },
          signalOption(input.signal),
        ),
      );
      messageId = requiredId(waiter.messageId, "message_id");
    } else {
      // A lost response can still have created a conversation, so this mutation is never retried.
      const waiter = await this.callMutation<
        Awaited<ReturnType<DatabricksGenieSdkClient["genieStartConversation"]>>
      >(() =>
        this.client.genieStartConversation(
          { spaceId: this.agentId, ...payload },
          signalOption(input.signal),
        ),
      );
      conversationId = requiredId(waiter.conversationId, "conversation_id");
      messageId = requiredId(waiter.messageId, "message_id");
    }

    const message = await this.waitForMessage(conversationId, messageId, {
      ...(input.signal ? { signal: input.signal } : {}),
    });
    const parsed = await this.parseAttachments(conversationId, messageId, message, input.signal);
    const text = parsed.attachments
      .filter(
        (attachment): attachment is DatabricksGenieTextAttachment => attachment.kind === "text",
      )
      .map((attachment) => attachment.content)
      .join("\n");
    if (text.length > this.maxTextChars)
      throw new DatabricksGenieResponseLimitError("answer text", this.maxTextChars);
    const firstQuery = parsed.attachments.find(
      (attachment): attachment is DatabricksGenieQueryAttachment => attachment.kind === "query",
    );

    return {
      agentId: this.agentId,
      conversationId,
      messageId,
      status: "COMPLETED",
      ...parsed,
      ...(text ? { text } : {}),
      ...(firstQuery?.query ? { sql: firstQuery.query } : {}),
      ...(firstQuery ? { result: firstQuery.result.raw } : {}),
    };
  }

  private async callMutation<T>(call: () => Promise<T>): Promise<T> {
    try {
      return await call();
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      if (/response field .* required for polling is missing/iu.test(message))
        throw new DatabricksGenieProtocolError(message);
      throw error;
    }
  }

  async waitForMessage(
    conversationId: string,
    messageId: string,
    options: { signal?: AbortSignal } = {},
  ): Promise<RawGenieMessage> {
    const timeoutMs = this.timeoutMs;
    const deadline = Date.now() + timeoutMs;
    while (true) {
      throwIfAborted(options.signal);
      const message = snakeRecord(
        await this.client.genieGetConversationMessage(
          { spaceId: this.agentId, conversationId, messageId },
          signalOption(options.signal),
        ),
      ) as RawGenieMessage;
      const status = requiredStatus(message.status);
      if (status === "COMPLETED") return message;
      if (FAILURE_STATES.has(status)) {
        throw new DatabricksGenieMessageError(
          conversationId,
          messageId,
          status,
          asRecord(message.error),
        );
      }
      const remaining = deadline - Date.now();
      if (remaining <= 0)
        throw new DatabricksGenieTimeoutError(conversationId, messageId, timeoutMs);
      await abortableSleep(
        Math.min(jitter(this.pollIntervalMs, this.pollJitterRatio), remaining),
        options.signal,
      );
    }
  }

  async getMessageAttachmentQueryResult(
    conversationId: string,
    messageId: string,
    attachmentId: string,
    options: { signal?: AbortSignal } = {},
  ): Promise<DatabricksGenieQueryResult> {
    const normalizedAttachmentId = nonEmpty(attachmentId, "attachmentId");
    let raw: Record<string, unknown>;
    try {
      raw = snakeRecord(
        await this.client.genieGetMessageAttachmentQueryResult(
          {
            spaceId: this.agentId,
            conversationId,
            messageId,
            attachmentId: normalizedAttachmentId,
          },
          signalOption(options.signal),
        ),
      );
    } catch (error) {
      if (!this.rawProtocolClient || !isGeneratedQueryResultSchemaError(error)) throw error;
      raw = await this.rawProtocolClient.get<Record<string, unknown>>(
        `${this.messagePath(conversationId, messageId)}/attachments/${enc(normalizedAttachmentId)}/query-result`,
        {},
        signalOption(options.signal),
      );
    }
    assertJsonSize(raw, this.maxResultBytes, "query result");
    return {
      ...optional("statementResponse", asRecord(raw.statement_response)),
      raw,
    };
  }

  async listConversations(
    options: {
      includeAll?: boolean;
      pageSize?: number;
      pageToken?: string;
      signal?: AbortSignal;
    } = {},
  ): Promise<DatabricksGenieConversationPage> {
    const response = snakeRecord(
      await this.client.genieListConversations(
        {
          spaceId: this.agentId,
          includeAll: options.includeAll,
          pageSize: options.pageSize,
          pageToken: options.pageToken,
        },
        signalOption(options.signal),
      ),
    );
    const conversations = asRecordArray(response.conversations).map((conversation) => ({
      conversationId: requiredId(
        conversation.conversation_id ?? conversation.id,
        "conversation_id",
      ),
      ...optional("title", asString(conversation.title)),
      ...optional("createdTimestamp", asNumber(conversation.created_timestamp)),
    }));
    return {
      conversations,
      ...optional("nextPageToken", asString(response.next_page_token)),
    };
  }

  async listMessages(
    conversationId: string,
    options: { pageSize?: number; pageToken?: string; signal?: AbortSignal } = {},
  ): Promise<DatabricksGenieMessagePage> {
    const response = snakeRecord(
      await this.client.genieListConversationMessages(
        {
          spaceId: this.agentId,
          conversationId,
          pageSize: options.pageSize,
          pageToken: options.pageToken,
        },
        signalOption(options.signal),
      ),
    );
    return {
      messages: asRecordArray(response.messages).map(parseMessageSummary),
      ...optional("nextPageToken", asString(response.next_page_token)),
    };
  }

  async createMessageComment(
    conversationId: string,
    messageId: string,
    content: string,
    options: { signal?: AbortSignal } = {},
  ): Promise<DatabricksGenieMessageComment> {
    const response = snakeRecord(
      await this.client.genieCreateMessageComment(
        {
          spaceId: this.agentId,
          conversationId,
          messageId,
          content: nonEmpty(content, "content"),
        },
        signalOption(options.signal),
      ),
    );
    return parseComment(response);
  }

  async listMessageComments(
    conversationId: string,
    messageId: string,
    options: { pageSize?: number; pageToken?: string; signal?: AbortSignal } = {},
  ): Promise<DatabricksGenieCommentPage> {
    const response = snakeRecord(
      await this.client.genieListMessageComments(
        {
          spaceId: this.agentId,
          conversationId,
          messageId,
          pageSize: options.pageSize,
          pageToken: options.pageToken,
        },
        signalOption(options.signal),
      ),
    );
    return {
      comments: asRecordArray(response.comments).map(parseComment),
      ...optional("nextPageToken", asString(response.next_page_token)),
    };
  }

  async deleteConversation(
    conversationId: string,
    options: { signal?: AbortSignal } = {},
  ): Promise<void> {
    await this.client.genieDeleteConversation(
      { spaceId: this.agentId, conversationId },
      signalOption(options.signal),
    );
  }

  private async parseAttachments(
    conversationId: string,
    messageId: string,
    message: RawGenieMessage,
    signal?: AbortSignal,
  ): Promise<ParsedAttachments> {
    const rawAttachments = Array.isArray(message.attachments) ? message.attachments : [];
    if (rawAttachments.length > this.maxAttachments)
      throw new DatabricksGenieResponseLimitError("attachment count", this.maxAttachments);

    const attachments: DatabricksGenieAttachment[] = [];
    const unknownAttachments: DatabricksGenieUnknownAttachment[] = [];
    let unknownAttachmentsTruncated = false;
    for (const rawValue of rawAttachments) {
      const raw = asRecord(rawValue) as RawGenieAttachment | undefined;
      if (!raw) {
        unknownAttachmentsTruncated = this.pushUnknown(unknownAttachments, {
          raw: { value: rawValue },
        });
        continue;
      }
      const attachmentId = asString(raw.attachment_id);
      const text = asRecord(raw.text);
      const query = asRecord(raw.query);
      const viz = asRecord(raw.viz);
      const suggested = asRecord(raw.suggested_questions);
      if (text && asString(text.content) !== undefined) {
        attachments.push({
          kind: "text",
          ...(attachmentId ? { attachmentId } : {}),
          content: asString(text.content) ?? "",
        });
      } else if (query) {
        if (!attachmentId)
          throw new DatabricksGenieProtocolError("query attachment did not contain attachment_id");
        const result = await this.getMessageAttachmentQueryResult(
          conversationId,
          messageId,
          attachmentId,
          { ...(signal ? { signal } : {}) },
        );
        attachments.push({
          kind: "query",
          attachmentId,
          ...optional("query", asString(query.query)),
          ...optional("title", asString(query.title)),
          ...optional("description", asString(query.description)),
          ...optional("statementId", asString(query.statement_id)),
          ...optional("queryResultMetadata", asRecord(query.query_result_metadata)),
          result,
        });
      } else if (viz) {
        if (!attachmentId)
          throw new DatabricksGenieProtocolError(
            "visualization attachment did not contain attachment_id",
          );
        attachments.push({
          kind: "visualization",
          attachmentId,
          ...optional("title", asString(viz.title)),
          ...optional("queryAttachmentId", asString(viz.query_attachment_id)),
          downloadPath: `${this.messagePath(conversationId, messageId)}/attachments/${enc(attachmentId)}/download-visualization`,
        });
      } else if (suggested && Array.isArray(suggested.questions)) {
        attachments.push({
          kind: "suggested-questions",
          ...(attachmentId ? { attachmentId } : {}),
          questions: suggested.questions.filter(
            (question): question is string => typeof question === "string",
          ),
        });
      } else {
        unknownAttachmentsTruncated =
          this.pushUnknown(unknownAttachments, {
            ...(attachmentId ? { attachmentId } : {}),
            raw,
          }) || unknownAttachmentsTruncated;
      }
    }
    return { attachments, unknownAttachments, unknownAttachmentsTruncated };
  }

  private pushUnknown(
    target: DatabricksGenieUnknownAttachment[],
    attachment: Omit<DatabricksGenieUnknownAttachment, "kind">,
  ): boolean {
    if (target.length >= this.maxUnknownAttachments) return true;
    assertJsonSize(attachment.raw, this.maxResultBytes, "unknown attachment");
    target.push({ kind: "unknown", ...attachment });
    return false;
  }

  private conversationPath(conversationId: string): string {
    return `${this.basePath}/conversations/${enc(nonEmpty(conversationId, "conversationId"))}`;
  }

  private messagePath(conversationId: string, messageId: string): string {
    return `${this.conversationPath(conversationId)}/messages/${enc(nonEmpty(messageId, "messageId"))}`;
  }
}

function isGeneratedQueryResultSchemaError(error: unknown): boolean {
  const message = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
  return /ZodError/i.test(message) && /statement_response[\s\S]*data_array/i.test(message);
}

export function databricksGenie(
  client: DatabricksGenieSdkClient,
  options: DatabricksGenieClientOptions,
): DatabricksGenieClient {
  return new DatabricksGenieClient(client, options);
}

/**
 * Genie Agents — delegate a natural-language analytics question to a governed Genie Agent. Genie
 * generates and runs SQL on the warehouse and returns typed answer, query, result, and visualization
 * attachments. Pass `conversationId` to continue a multi-turn thread.
 */
export function databricksGenieTool(
  client: DatabricksGenieSdkClient,
  options: DatabricksGenieToolOptions,
): ToolDef<{ question: string; conversationId?: string }, DatabricksGenieResponse> {
  const genie = databricksGenie(client, options);
  return {
    name: options.name ?? "databricks_genie_ask",
    description:
      options.description ??
      "Ask a natural-language analytics question in a Databricks Genie Agent; returns typed answer, SQL, result, and visualization attachments.",
    inputSchema: {
      type: "object",
      additionalProperties: false,
      properties: { question: { type: "string" }, conversationId: { type: "string" } },
      required: ["question"],
    },
    metadata: { effect: "read", provider: "databricks", service: "genie" },
    execute(input, context?: ToolContext) {
      return genie.ask({
        question: input.question,
        ...(input.conversationId ? { conversationId: input.conversationId } : {}),
        ...(options.enableVisualization !== undefined
          ? { enableVisualization: options.enableVisualization }
          : {}),
        ...(context?.signal ? { signal: context.signal } : {}),
      });
    },
  };
}

function resolveAgentId(options: DatabricksGenieAgentReference): string {
  return nonEmpty(options.agentId.trim(), "agentId");
}

function parseMessageSummary(message: Record<string, unknown>): DatabricksGenieMessageSummary {
  return {
    messageId: requiredId(message.message_id ?? message.id, "message_id"),
    ...optional("conversationId", asString(message.conversation_id)),
    ...optional("content", asString(message.content)),
    ...optional("status", asString(message.status)),
    ...optional("createdTimestamp", asNumber(message.created_timestamp)),
    ...optional("lastUpdatedTimestamp", asNumber(message.last_updated_timestamp)),
    ...optional("error", asRecord(message.error)),
  };
}

function parseComment(comment: Record<string, unknown>): DatabricksGenieMessageComment {
  return {
    messageCommentId: requiredId(comment.message_comment_id ?? comment.id, "message_comment_id"),
    content: asString(comment.content) ?? "",
    ...optional("conversationId", asString(comment.conversation_id)),
    ...optional("messageId", asString(comment.message_id)),
    ...optional("createdTimestamp", asNumber(comment.created_timestamp)),
    ...optional("userId", asNumber(comment.user_id)),
  };
}

function requiredId(value: unknown, name: string): string {
  const id = asString(value)?.trim();
  if (!id) throw new DatabricksGenieProtocolError(`response did not contain ${name}`);
  return id;
}

function requiredStatus(value: unknown): DatabricksGenieMessageStatus {
  const status = asString(value)?.trim();
  if (!status) throw new DatabricksGenieProtocolError("message response did not contain status");
  return status;
}

function nonEmpty(value: string, name: string): string {
  const normalized = value.trim();
  if (!normalized) throw new Error(`Databricks Genie ${name} cannot be empty.`);
  return normalized;
}

function positiveInteger(value: number, name: string): number {
  if (!Number.isInteger(value) || value <= 0)
    throw new Error(`Databricks Genie ${name} must be a positive integer.`);
  return value;
}

function nonNegativeInteger(value: number, name: string): number {
  if (!Number.isInteger(value) || value < 0)
    throw new Error(`Databricks Genie ${name} must be a non-negative integer.`);
  return value;
}

function boundedRatio(value: number, name: string): number {
  if (!Number.isFinite(value) || value < 0 || value > 1)
    throw new Error(`Databricks Genie ${name} must be between 0 and 1.`);
  return value;
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

function asRecordArray(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value)
    ? value.filter(
        (entry): entry is Record<string, unknown> =>
          typeof entry === "object" && entry !== null && !Array.isArray(entry),
      )
    : [];
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

function asNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function signalOption(signal?: AbortSignal): { signal?: AbortSignal } {
  return signal ? { signal } : {};
}

function optional<Key extends string, Value>(
  key: Key,
  value: Value | undefined,
): Partial<Record<Key, Value>> {
  return value === undefined ? {} : ({ [key]: value } as Record<Key, Value>);
}

function assertJsonSize(value: unknown, maxBytes: number, kind: string): void {
  const bytes = new TextEncoder().encode(JSON.stringify(value)).byteLength;
  if (bytes > maxBytes) throw new DatabricksGenieResponseLimitError(kind, maxBytes);
}

function jitter(intervalMs: number, ratio: number): number {
  if (ratio === 0) return intervalMs;
  const spread = intervalMs * ratio;
  return Math.max(1, Math.round(intervalMs - spread + Math.random() * spread * 2));
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted)
    throw signal.reason ?? new DOMException("The operation was aborted.", "AbortError");
}

function abortableSleep(ms: number, signal?: AbortSignal): Promise<void> {
  throwIfAborted(signal);
  if (!signal) return new Promise((resolve) => setTimeout(resolve, ms));
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      signal.removeEventListener("abort", abort);
      resolve();
    }, ms);
    const abort = () => {
      clearTimeout(timer);
      reject(signal.reason ?? new DOMException("The operation was aborted.", "AbortError"));
    };
    signal.addEventListener("abort", abort, { once: true });
  });
}

function errorDetail(details?: Record<string, unknown>): string {
  const message = details && asString(details.message);
  return message ? `: ${message.slice(0, 500)}` : "";
}

function snakeRecord(value: unknown): Record<string, unknown> {
  return snakeValue(value) as Record<string, unknown>;
}

function snakeValue(value: unknown): unknown {
  if (typeof value === "bigint") {
    const number = Number(value);
    return Number.isSafeInteger(number) ? number : value.toString();
  }
  if (Array.isArray(value)) return value.map(snakeValue);
  if (value && typeof value === "object") {
    const source = value as Record<string, unknown>;
    if (typeof source.$case === "string" && source[source.$case] !== undefined) {
      return {
        [source.$case.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)]: snakeValue(
          source[source.$case],
        ),
      };
    }
    const record = Object.fromEntries(
      Object.entries(value).map(([key, item]) => [
        key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`),
        snakeValue(item),
      ]),
    );
    const attachment = record.attachment;
    if (attachment && typeof attachment === "object" && !Array.isArray(attachment)) {
      const { attachment: _attachment, ...withoutAttachment } = record;
      return { ...withoutAttachment, ...attachment };
    }
    return record;
  }
  return value;
}
